import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Select, Toast } from '@douyinfe/semi-ui';
import { IconSearch } from '@douyinfe/semi-icons';
import GroupSelector from '../../../components/GroupSelector';
import UserSelector from '../../../components/UserSelector';
import { useNavigate } from 'react-router-dom';
import request from '../../../utils/request';
import styles from './index.module.scss';

const OrderList = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [searchGroupUids, setSearchGroupUids] = useState('');
    const [searchSuUids, setSearchSuUids] = useState('');
    const [searchFromSuUids, setSearchFromSuUids] = useState('');

    const statusMap = {
        0: { text: '未付款', type: 'warning' },
        1: { text: '已付款', type: 'success' },
    };

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/join_group/list_jg_order/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus,
                    group_uids: searchGroupUids,
                    su_uids: searchSuUids,
                    from_su_uids: searchFromSuUids
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // 从URL获取group_uids参数
        const searchParams = new URLSearchParams(window.location.search);
        const groupUids = searchParams.get('group_uids');
        if (groupUids) {
            setSearchGroupUids(groupUids);
        }
    }, []);

    useEffect(() => {
        fetchList(1, pagination.pageSize);
    }, [searchGroupUids]);

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '群信息',
            dataIndex: 'group',
            width: 200,
            render: (text, record) => (
                <div style={{ fontSize: '12px' }}>
                    <div style={{ color: 'var(--semi-color-text-0)', marginBottom: '4px' }}>{text?.name || '-'}</div>
                </div>
            )
        },
        {
            title: '用户信息',
            dataIndex: 'su',
            width: 200,
            render: (text) => (
                <div
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                    onClick={() => text?.uid && navigate(`/user/${text.uid}`)}
                >
                    {text?.avatar && <img src={text.avatar} alt="avatar" style={{ width: 24, height: 24, borderRadius: '50%' }} />}
                    <div style={{ fontSize: '12px' }}>
                        <div>{text?.name || '-'}</div>
                    </div>
                </div>
            )
        },
        {
            title: '推荐人',
            dataIndex: 'from_su',
            width: 200,
            render: (text) => (
                <div
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                    onClick={() => text?.uid && navigate(`/user/${text.uid}`)}
                >
                    {text?.avatar && <img src={text.avatar} alt="avatar" style={{ width: 24, height: 24, borderRadius: '50%' }} />}
                    <div style={{ fontSize: '12px' }}>
                        <div>{text?.name || '-'}</div>
                    </div>
                </div>
            )
        },
        {
            title: '支付状态',
            dataIndex: 'status',
            width: 120,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '支付金额',
            dataIndex: 'price',
            width: 120,
            render: (text) => `¥${(text / 100).toFixed(2)}`
        },
        {
            title: '备注',
            dataIndex: 'title',
            width: 200
        },
        {
            title: 'IP地址',
            dataIndex: 'last_ip',
            width: 150
        },
        {
            title: '创建时间/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        }
    ];

    return (
        <div className={styles.orderListContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索备注"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Select
                        placeholder="支付状态"
                        value={searchStatus}
                        onChange={setSearchStatus}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    <div style={{ width: 200 }}>
                        <GroupSelector
                            value={searchGroupUids ? searchGroupUids.split(',') : []}
                            onChange={(value) => setSearchGroupUids(value ? value.join(',') : '')}
                        />
                    </div>
                    <div style={{ width: 200 }}>
                        <UserSelector
                            value={searchSuUids ? searchSuUids.split(',') : []}
                            onChange={(value) => setSearchSuUids(value ? value.join(',') : '')}
                            placeholder="请选择用户"
                        />
                    </div>
                    <div style={{ width: 200 }}>
                        <UserSelector
                            value={searchFromSuUids ? searchFromSuUids.split(',') : []}
                            onChange={(value) => setSearchFromSuUids(value ? value.join(',') : '')}
                            placeholder="请选择推荐人"
                        />
                    </div>
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                scroll={{ x: 'max-content' }}
            />
        </div>
    );
};

export default OrderList;